<template>
    <Button
        unstyled
        :pt="theme"
        :ptOptions="{
            mergeProps: ptViewMerge
        }"
    >
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
    </Button>
</template>

<script setup lang="ts">
import Button, { type ButtonPassThroughOptions, type ButtonProps } from 'primevue/button';
import { ref } from 'vue';
import { ptViewMerge } from './utils';

interface Props extends /* @vue-ignore */ ButtonProps {}
defineProps<Props>();

const theme = ref<ButtonPassThroughOptions>({
    root: `inline-flex cursor-pointer select-none items-center justify-center overflow-hidden relative
        px-3 py-2 gap-2 rounded-md disabled:pointer-events-none disabled:opacity-60 transition-colors duration-200
        bg-navy-900 enabled:hover:bg-navy-950 enabled:active:bg-navy-950
        border border-navy-900 enabled:hover:border-navy-950 enabled:active:border-navy-950
        text-white enabled:hover:text-white enabled:active:text-white
        dark:bg-navy-800 dark:enabled:hover:bg-navy-700 dark:enabled:active:bg-navy-600
        dark:border-navy-800 dark:enabled:hover:border-navy-700 dark:enabled:active:border-navy-600
        dark:text-white dark:enabled:hover:text-white dark:enabled:active:text-white
        focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2
        focus-visible:outline-navy-900 dark:focus-visible:outline-navy-300
        p-vertical:flex-col p-fluid:w-full p-fluid:p-icon-only:w-10
        p-icon-only:w-10 p-icon-only:px-0 p-icon-only:gap-0
        p-icon-only:p-rounded:rounded-full p-icon-only:p-rounded:h-10
        p-small:text-sm p-small:px-[0.625rem] p-small:py-[0.375rem]
        p-large:text-[1.125rem] p-large:px-[0.875rem] p-large:py-[0.625rem]
        p-raised:shadow-sm p-rounded:rounded-full
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-navy-50 enabled:active:p-outlined:bg-navy-100
        p-outlined:border-navy-200 enabled:hover:p-outlined:border-navy-200 enabled:active:p-outlined:border-navy-200
        p-outlined:text-navy-900 enabled:hover:p-outlined:text-navy-900 enabled:active:p-outlined:text-navy-900
        dark:p-outlined:bg-transparent dark:enabled:hover:p-outlined:bg-white/5 dark:enabled:active:p-outlined:bg-white/15
        dark:p-outlined:border-navy-700 dark:enabled:hover:p-outlined:border-navy-700 dark:enabled:active:p-outlined:border-navy-600
        dark:p-outlined:text-white dark:enabled:hover:p-outlined:text-white dark:enabled:active:p-outlined:text-white
        p-text:bg-transparent enabled:hover:p-text:bg-navy-50 enabled:active:p-text:bg-navy-100
        p-text:border-transparent enabled:hover:p-text:border-transparent enabled:active:p-text:border-transparent
        p-text:text-navy-900 enabled:hover:p-text:text-navy-900 enabled:active:p-text:text-navy-900
        dark:p-text:bg-transparent dark:enabled:hover:p-text:bg-navy-800 dark:enabled:active:p-text:bg-navy-700
        dark:p-text:border-transparent dark:enabled:hover:p-text:border-transparent dark:enabled:active:p-text:border-transparent
        dark:p-text:text-white dark:enabled:hover:p-text:text-white dark:enabled:active:p-text:text-white
    `,
    loadingIcon: `animate-spin`,
    icon: `p-right:order-1 p-bottom:order-2`,
    label: `font-medium p-icon-only:invisible p-icon-only:w-0
        p-small:text-sm p-large:text-[1.125rem]`,
    pcBadge: {
        root: `min-w-4 h-4 leading-4`
    }
});
</script>
